<template>
  <form
    id="planning-modal-step-one"
    :aria-label="t('routeBuilder.routePlanning')"
    class="planning-modal-step-one"
  >
    <p id="route-planning-type-selector-label">
      {{ t('routeBuilder.selectRoutePlan') }}
    </p>

    <Listbox
      id="route-planning-type-selector"
      v-model="internalValue"
      class="route-planning-type-selector"
      :options="options"
      :pt="{
        list: {
          'aria-labelledby': 'route-planning-type-selector-label',
          'aria-required': 'true',
          'aria-describedby': errorObject?.$uid,
          'aria-invalid': errorObject ? 'true' : 'false',
          'aria-errormessage': errorObject?.$uid,
        },
      }"
      option-value="value"
      list-style="max-height: 100%"
      autofocus
      @option-dblclick="onOptionDoubleClick"
    >
      <template #option="{ option }">
        <PlanningModalOption :option="option" :ship-orders="shipOrders" />
      </template>
    </Listbox>

    <InputError
      v-if="errorObject"
      :key="errorObject.$uid"
      :error="errorObject"
    />
  </form>
</template>

<script lang="ts" setup>
import { ErrorObject } from '@vuelidate/core';
import type { PropType, Ref } from 'vue';
import { computed, nextTick, ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { translateEnum } from '@rygen/rygen-ui/i18n';

import { RoutePlanType, ValidShipOrder } from '@/model/routeBuilder';

import PlanningModalOption from '@/components/routeBuilder/planningPool/PlanningModalOption.vue';
import InputError from '@rygen/rygen-ui/components/form/InputError.vue';
import Listbox, { ListboxOptionDblClickEvent } from 'primevue/listbox';

const internalValue = defineModel({
  type: String as PropType<RoutePlanType | undefined>,
  default: () => undefined,
});

const props = defineProps({
  shipOrders: {
    type: Array as PropType<ValidShipOrder[]>,
    default: () => [],
  },
  availableRoutePlanTypes: {
    type: Array as PropType<RoutePlanType[]>,
    default: () => [],
  },
});
const { shipOrders, availableRoutePlanTypes } = toRefs(props);
const emit = defineEmits<{
  (event: 'option-dblclick', newVal: RoutePlanType): void;
}>();

const { t } = useI18n();

const errorMessage: Ref<string> = ref('');

const options = computed(() => {
  return availableRoutePlanTypes.value.map((t) => ({
    label: translateEnum(t, 'RoutePlanType'),
    value: t,
  }));
});

const errorObject: Ref<ErrorObject | undefined> = computed(() => {
  if (!errorMessage.value) {
    return undefined;
  }

  return {
    $propertyPath: '.',
    $property: 'routePlanType',
    $validator: 'manual',
    $message: errorMessage.value,
    $params: {},
    $pending: false,
    $response: '',
    $uid: 'routePlanType-required',
  };
});

const validate = async () => {
  if (!internalValue.value) {
    errorMessage.value = t('validations.required', {
      label: t('common.paramTypes', { param: t('routeBuilder.routePlanning') }),
    });
    return false;
  }

  errorMessage.value = '';
  return true;
};

const onOptionDoubleClick = (event: ListboxOptionDblClickEvent) => {
  // Prevent the double click from selecting the text
  event.originalEvent.preventDefault();
  const selection = window.getSelection();
  selection?.removeAllRanges();

  const selectedValue = event.value as (typeof options.value)[0];
  internalValue.value = selectedValue.value;

  nextTick(() => {
    emit('option-dblclick', selectedValue.value);
  });
};

watch(internalValue, (newVal) => {
  if (newVal) {
    errorMessage.value = '';
  }
});

defineExpose({
  validate,
});
</script>

<style>
.planning-modal-step-one {
  --rygen-modal-max-width: 700px;
  --p-listbox-background: white;
  --p-listbox-shadow: none;
  --p-listbox-list-gap: var(--gap-1);
  --p-listbox-option-selected-background: initial;
  --p-listbox-option-selected-color: initial;
  --p-listbox-option-color: var(--p-gray-700);
  --p-listbox-option-border-radius: var(--p-border-radius-md);

  --listbox-option-border-color: var(--divider-color);

  .route-planning-type-selector {
    margin-top: var(--sp-6);
    border: none;

    .p-listbox-option {
      border: 1px solid var(--listbox-option-border-color);

      &.p-listbox-option-selected {
        --listbox-option-border-color: var(--p-blue-500);
      }
    }
  }
}
</style>
